/**
 * GLSL functions for rendering design material SDF patterns that have been converted to a single normalized heightmap.
 * Computes similar output variables as sdf.ts.
 * @module
 */
import { arcVisibilityUniformWeightDeclaration } from "./utils";

export function parallaxUvOnly(_initUVSpaceTanRay: string, _applyDetailTgtNormal: string) {
  return `
uvWithParallax = vUv;
if (parallaxScale > 0.0) {
  vec2 parallaxUvScale = 1.0 / parallaxMapWorldSize;
  mat2 rotationMat = rot(parallaxMapRotation);
  vec2 rotatedUv = rotationMat * vUv;
  parallaxMapUvh = vec3(rotatedUv * parallaxUvScale, 1.0);
}`;
}

export const preLightParallaxMap = (initUVSpaceTanRay: string, applyDetailTgtNormal: string) => {
  return `

mat2 rotationMat = rot(parallaxMapRotation);
mat2 invRotationMat = transpose(rotationMat);
vec2 rotatedUv = rotationMat * vUv;
vec2 parallaxUvScale = 1.0 / parallaxMapWorldSize;

if (parallaxScale > 0.0) {
  ${initUVSpaceTanRay}

  float parallaxMapWorldAspect = parallaxMapWorldSize.y / parallaxMapWorldSize.x;
  vec3 parallaxRay = vec3(rotationMat * tanRay.xy, tanRay.z);
  parallaxRay = normalize(vec3(parallaxRay.x * parallaxMapWorldAspect, parallaxRay.yz));
  parallaxMapUvh = parallaxMap(rotatedUv * parallaxUvScale, parallaxRay);

  vec2 uStep = vec2(1.0, 0.0) * parallaxMapInvSize;
  vec2 vStep = vec2(0.0, 1.0) * parallaxMapInvSize;
  float right = texture2DLodEXT(parallaxBumpMap, parallaxMapUvh.xy + uStep, 0.0).r;
  float left = texture2DLodEXT(parallaxBumpMap, parallaxMapUvh.xy - uStep, 0.0).r;
  float up = texture2DLodEXT(parallaxBumpMap, parallaxMapUvh.xy + vStep, 0.0).r;
  float down = texture2DLodEXT(parallaxBumpMap, parallaxMapUvh.xy - vStep, 0.0).r;

  vec3 detailTgtNormal = normalize(vec3((left - right) * parallaxMapNormalScale * parallaxMapWorldAspect, (down - up) * parallaxMapNormalScale, 2.0));
  rotationMat[0][1] = -rotationMat[0][1];
  rotationMat[1][0] = -rotationMat[1][0];
  detailTgtNormal = vec3(rotationMat * detailTgtNormal.xy, detailTgtNormal.z);
  ${applyDetailTgtNormal}

  // Compute AO.
  // TODO: Baking AO into a texture could be better than evaluating it inline here in the shader.
  vec2 horizons = vec2(-3.14159 * 0.49);

  float searchAngle = rand(gl_FragCoord.xy + randomSeed) * 3.14159;
  float uvStepFromWorldStep = 0.5 / parallaxMapWorldSize.y;
  vec2 searchStepUvLength = vec2(max(uvStepFromWorldStep, 1.5 * parallaxMapInvSize));
  vec2 searchStep = vec2(cos(searchAngle) * parallaxMapWorldAspect, sin(searchAngle)) * searchStepUvLength;

  float centerHeight = parallaxMapUvh.z;

  vec2 aoSearchCenter = parallaxMapUvh.xy + vec2(randomSeed) * parallaxMapInvSize;

  // Adding this offset trades some regular artifacts with random noise.
  float offset = (rand(gl_FragCoord.xy + 0.5 + randomSeed) - 0.5) * 0.75;

  float heightmapWorldZDepth = parallaxScale * parallaxMapWorldSize.y;

  float weightedCurv = 0.0;
  float edgeDist = 100.0;

  for (float i = 1.0; i < 5.0; ++i) {
    vec2 heights;
    offset = -offset;
    heights.x = texture2DLodEXT(parallaxBumpMap, aoSearchCenter + searchStep * (i + offset), 0.0).r;
    heights.y = texture2DLodEXT(parallaxBumpMap, aoSearchCenter - searchStep * (i + offset), 0.0).r;
    vec2 horizonCandidates = atan((heights - centerHeight) * parallaxScale, searchStepUvLength * i);
    horizons = max(horizons, horizonCandidates);
    float edginess = max(max(centerHeight - heights.y, centerHeight - heights.x) * heightmapWorldZDepth - 0.1, 0.0);
    weightedCurv += min(edginess, 0.3) / (i + 1.5);
  }

  horizons = 3.14159 * 0.5 - horizons; // Horizon values are between 0 and PI. Larger values mean more open angle.

  float arcVisibility = arcVisibilityUniformWeight(horizons.x, horizons.y);

  detailSelfAO = mix(1.0, min(arcVisibility * 0.5 + 0.05, 1.0), parallaxAoStrength);
  edgeWearMask = max(weightedCurv * edgeWearMaskParams.x - edgeWearMaskParams.y, 0.0);

#if NUM_DIR_LIGHTS > 0
  if (useParallaxShadows > 0.5) {
    vec3 lightRay = directionalLights[0].direction * dmTbn;
    vec3 parallaxLightRay = vec3(rotationMat * lightRay.xy, -lightRay.z);
    parallaxLightRay = normalize(vec3(parallaxLightRay.x * parallaxMapWorldAspect, parallaxLightRay.yz));
    detailSelfShadow = getParallaxShadow(parallaxMapUvh, parallaxLightRay);
  }
#endif
} else {
  ${
    ""
    // parallaxMapUvh.z is set to 0 when parallax is disabled so that we avoid extra random mask samples. Otherwise it's not meaningful
  }
  parallaxMapUvh = vec3(rotatedUv * parallaxUvScale, 0.0);
}
uvWithParallax = invRotationMat * (parallaxMapUvh.xy * parallaxMapWorldSize);
`;
};

export const PARALLAX_MAX_ITERATIONS = 20;
export const PARALLAX_RELIEF_SEARCH_STEPS = 4;

export const parallaxMapDeclarations = `
uniform sampler2D parallaxBumpMap;
uniform float parallaxMapInvSize;
uniform vec2 parallaxMapWorldSize;
uniform float parallaxMapRotation;

uniform float parallaxScale; // How deep the parallax map is in parallax uv space.
uniform float parallaxMapNormalScale; // should be set to parallaxScale * parallaxMapSize * normal strength factor

uniform float parallaxAoStrength;
uniform float useParallaxShadows;

uniform vec2 edgeWearMaskParams; // Multiplier and threshold packed into vec2

vec3 parallaxMapUvh; // The z coordinate contains the normalized height sampled from the parallax map.

${arcVisibilityUniformWeightDeclaration}

mat2 rot(in float a) {
  float c = cos(a), s = sin(a);
  return mat2(c, s, -s, c);
}

float getParallaxShadow(vec3 parallaxUvh, vec3 parallaxRay) {
  vec3 currentRayCoords = parallaxUvh;
  vec3 step = vec3(parallaxRay.xy * parallaxScale, -parallaxRay.z);

  step *= parallaxMapInvSize / length(step.xy);

  currentRayCoords += step;

  for (int i = 0; i < 10; i += 1) {
    float heightFromTexture = texture2DLodEXT(parallaxBumpMap, currentRayCoords.xy, 0.0).r;
    if (heightFromTexture > currentRayCoords.z) {
      return 0.0;
    }
    currentRayCoords += step;
  }
  return 1.0;
}

vec3 parallaxMap( in vec2 uv, in vec3 V ) {

#if !defined(PARALLAX_EFFECT_ENABLED) || PARALLAX_EFFECT_ENABLED != 1
  return vec3(uv, texture2DLodEXT(parallaxBumpMap, uv, 0.0).r);
#else

  vec3 currentRayCoords = vec3(uv, parallaxScale);

  vec3 step = V * parallaxMapInvSize;
  // Step is now about 1 pixel long in the x/y dimensions.

  // Allow longer steps in terms of pixels if the ray is hitting the texture at a grazing angle.
  float maxStep = parallaxMapInvSize * mix(8.0, 1.0, abs(V.z));

  float heightFromTexture = texture2DLodEXT(parallaxBumpMap, currentRayCoords.xy, 0.0).r * parallaxScale;

  for (int i = 0; i < ${PARALLAX_MAX_ITERATIONS}; i += 1) {
    if ( heightFromTexture >= currentRayCoords.z ) {
      break;
    }
    // Shift texture coordinates along vector V
    step = V * min(currentRayCoords.z - heightFromTexture, maxStep);
    currentRayCoords += step;
    heightFromTexture = texture2DLodEXT(parallaxBumpMap, currentRayCoords.xy, 0.0).r * parallaxScale;
  }

  #if defined( USE_RELIEF_PARALLAX )

    // Return to the mid point of the previous step
    step = step * 0.5;
    currentRayCoords -= step;

    // Binary search to increase precision
    for (int i = 0; i < ${PARALLAX_RELIEF_SEARCH_STEPS}; i += 1) {
      step *= 0.5;
      heightFromTexture = texture2DLodEXT(parallaxBumpMap, currentRayCoords.xy, 0.0).r * parallaxScale;
      if (heightFromTexture > currentRayCoords.z) { // Below the surface
        currentRayCoords -= step;
      } else { // above the surface
        currentRayCoords += step;
      }
    }

  #endif // USE_RELIEF_PARALLAX

  return vec3(currentRayCoords.xy, heightFromTexture / parallaxScale);
#endif
}`;
